import React, { useMemo } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import type { StrapiCustomerRow } from '../api/strapiCrm';
import { incompleteCustomerFieldLabels } from '../pages/CustomersPage/customerCompleteness';
import { DataGridNumberedPagination, DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS } from './DataGridNumberedPagination';

const statusLabel: Record<string, string> = {
  active: 'Aktif',
  inactive: 'Tidak aktif',
  prospect: 'Prospek',
  blocked: 'Diblokir',
};

type CustomerDataGridProps = {
  rows: StrapiCustomerRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  onEdit: (row: StrapiCustomerRow) => void;
  onView: (row: StrapiCustomerRow) => void;
  onDelete: (row: StrapiCustomerRow) => void;
};

export function CustomerDataGrid({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  isLoading,
  isError,
  error,
  onEdit,
  onView,
  onDelete,
}: CustomerDataGridProps) {
  const columns: GridColDef<StrapiCustomerRow>[] = useMemo(
    () => [
      { field: 'customer_number', headerName: 'No. customer', minWidth: 120, flex: 0.5 },
      { field: 'name', headerName: 'Nama', minWidth: 160, flex: 1 },
      {
        field: 'phone',
        headerName: 'No. telp',
        minWidth: 118,
        flex: 0.45,
        valueFormatter: (v) => (v == null || v === '' ? '—' : String(v)),
      },
      {
        field: 'email',
        headerName: 'Email',
        minWidth: 160,
        flex: 0.7,
        valueFormatter: (v) => (v == null || v === '' ? '—' : String(v)),
      },
      {
        field: 'active_status',
        headerName: 'Status',
        minWidth: 110,
        flex: 0.4,
        valueFormatter: (v) =>
          v == null || v === '' ? '—' : (statusLabel[String(v)] ?? String(v)),
      },
      {
        field: '__actions',
        headerName: 'Aksi',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 176,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const missingLabels = incompleteCustomerFieldLabels(row);
          return (
          <Box
            sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {missingLabels.length > 0 ? (
              <Tooltip
                title={`Field belum diisi:\n${missingLabels.map((label) => `• ${label}`).join('\n')}`}
                slotProps={{ tooltip: { sx: { maxWidth: 360, whiteSpace: 'pre-line' } } }}
              >
                <IconButton
                  size="small"
                  color="warning"
                  aria-label={`Data ${row.name} belum lengkap`}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <IconButton size="small" aria-label={`Lihat ${row.name}`} onClick={() => onView(row)}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label={`Ubah ${row.name}`} onClick={() => onEdit(row)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              aria-label={`Hapus ${row.name}`}
              onClick={() => onDelete(row)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
          );
        },
      },
    ],
    [onEdit, onDelete, onView],
  );

  if (isError) {
    return <Alert severity="error">{error instanceof Error ? error.message : 'Gagal memuat pelanggan.'}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="customers-data-grid">
      {isLoading && rows.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.documentId}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[...DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS]}
          loading={isLoading}
          slots={{ pagination: DataGridNumberedPagination }}
          autoHeight
          density="compact"
          getRowClassName={(params) =>
            incompleteCustomerFieldLabels(params.row).length > 0 ? 'customer-row-incomplete' : ''
          }
          sx={{
            '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
            '& .MuiDataGrid-row.customer-row-incomplete': {
              bgcolor: '#fff3bf',
              '& .MuiDataGrid-cell': { bgcolor: '#fff3bf' },
              '&:hover, &.Mui-hovered': {
                bgcolor: '#fff3bf',
                '& .MuiDataGrid-cell': { bgcolor: '#fff3bf' },
              },
            },
          }}
        />
      )}
    </Box>
  );
}
